import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Eye, EyeOff, CheckCircle2, KeyRound, AlertTriangle, ArrowLeft } from "lucide-react";
import SEO from "../../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MIN_LEN = 12;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const valid = pw.length >= MIN_LEN && pw === confirm;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (pw.length < MIN_LEN) {
      setError(`Password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (pw !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: pw }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = typeof data?.detail === "string"
          ? data.detail
          : "Invalid or expired reset link.";
        setError(msg);
        return;
      }
      setDone(true);
    } catch (_) {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title="Set a new password · Ridgehawk Admin" path="/admin/reset-password" noindex />
      <div className="flex min-h-screen items-center justify-center bg-ridge-charcoal px-6 py-16" data-testid="reset-password-page">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center gap-3">
            <img src="/logo-icon.png" alt="" className="h-10 w-10 rounded bg-white p-0.5" />
            <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white">RIDGEHAWK · ADMIN</span>
          </div>

          <div className="rounded-md border border-white/10 bg-white p-7 shadow-[0_24px_48px_-24px_rgba(0,0,0,0.5)] md:p-8">
            {!token ? (
              <InvalidLink data-testid="reset-no-token" />
            ) : done ? (
              <div data-testid="reset-password-success">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-ridge-amber/15 text-ridge-amber">
                  <CheckCircle2 className="h-5 w-5" />
                </span>
                <h1 className="mt-4 font-display text-2xl font-bold tracking-tight text-ridge-charcoal md:text-3xl">
                  Password updated
                </h1>
                <p className="mt-3 text-sm leading-relaxed text-ridge-muted">
                  Your password has been changed. You can now sign in with your new password.
                </p>
                <Link
                  to="/admin/login"
                  className="btn-accent mt-6 w-full"
                  data-testid="reset-back-to-login"
                >
                  Sign in
                </Link>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="reset-password-form" noValidate>
                <h1 className="font-display text-2xl font-bold tracking-tight text-ridge-charcoal md:text-3xl">
                  Set a new password
                </h1>
                <p className="mt-2 text-sm text-ridge-muted">
                  Choose a password with at least <strong>{MIN_LEN} characters</strong>.
                </p>

                <div className="mt-6 space-y-4">
                  <PasswordField
                    id="reset-pw"
                    label="New password"
                    value={pw}
                    onChange={setPw}
                    show={showPw}
                    onToggle={() => setShowPw((v) => !v)}
                    autoFocus
                    testid="reset-password-new"
                  />
                  <PasswordField
                    id="reset-confirm"
                    label="Confirm new password"
                    value={confirm}
                    onChange={setConfirm}
                    show={showConfirm}
                    onToggle={() => setShowConfirm((v) => !v)}
                    testid="reset-password-confirm"
                  />
                  <PasswordChecklist pw={pw} confirm={confirm} />
                </div>

                {error ? (
                  <div
                    role="alert"
                    data-testid="reset-password-error"
                    className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
                  >
                    <p className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{error}</span>
                    </p>
                    {/expired|invalid/i.test(error) ? (
                      <p className="mt-2 ml-6 text-xs">
                        <Link
                          to="/admin/forgot-password"
                          className="font-semibold text-red-800 underline hover:no-underline"
                        >
                          Request a new reset link
                        </Link>
                      </p>
                    ) : null}
                  </div>
                ) : null}

                <button
                  type="submit"
                  className="btn-accent mt-6 w-full"
                  disabled={submitting || !valid}
                  data-testid="reset-password-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Updating…
                    </>
                  ) : (
                    <>
                      <KeyRound className="h-4 w-4" /> Update password
                    </>
                  )}
                </button>

                <p className="mt-5 text-center text-sm">
                  <Link
                    to="/admin/login"
                    className="inline-flex items-center gap-1.5 font-semibold text-ridge-charcoal transition-colors hover:text-ridge-amber"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back to sign in
                  </Link>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function PasswordField({ id, label, value, onChange, show, onToggle, testid, autoFocus = false }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-ridge-charcoal">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={show ? "text" : "password"}
          autoComplete="new-password"
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          minLength={MIN_LEN}
          data-testid={testid}
          className="block w-full rounded-md border border-ridge-border bg-white px-3.5 py-3 pr-12 text-sm focus:border-ridge-charcoal focus:outline-none focus:ring-2 focus:ring-ridge-charcoal/15 md:text-base"
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={show ? "Hide password" : "Show password"}
          data-testid={`${testid}-toggle`}
          className="absolute inset-y-0 right-2 inline-flex items-center justify-center px-2 text-ridge-muted hover:text-ridge-charcoal"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function PasswordChecklist({ pw, confirm }) {
  const checks = [
    { ok: pw.length >= MIN_LEN, label: `At least ${MIN_LEN} characters` },
    { ok: pw.length > 0 && pw === confirm, label: "Both passwords match" },
  ];
  return (
    <ul className="space-y-1 text-xs text-ridge-muted" data-testid="reset-checklist">
      {checks.map((c) => (
        <li key={c.label} className="flex items-center gap-2">
          <span className={`inline-block h-1.5 w-1.5 rounded-full ${c.ok ? "bg-ridge-amber" : "bg-ridge-border"}`} />
          <span className={c.ok ? "text-ridge-charcoal" : ""}>{c.label}</span>
        </li>
      ))}
    </ul>
  );
}

function InvalidLink() {
  return (
    <div data-testid="reset-invalid-link">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-700">
        <AlertTriangle className="h-5 w-5" />
      </span>
      <h1 className="mt-4 font-display text-2xl font-bold tracking-tight text-ridge-charcoal md:text-3xl">
        Invalid reset link
      </h1>
      <p className="mt-3 text-sm leading-relaxed text-ridge-muted">
        This reset link is missing a token, has already been used, or has expired.
        Please request a new one.
      </p>
      <Link
        to="/admin/forgot-password"
        className="btn-accent mt-6 w-full"
        data-testid="reset-request-new"
      >
        Request a new reset link
      </Link>
    </div>
  );
}
